import { Component, Input, Output, EventEmitter, OnInit, OnChanges, AfterViewInit, SimpleChanges, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LargeShareholderConnectionDto } from '../../../models/api/largeShareholderConnectionDto';
import { LargeShareholderConnectionType } from '../../../models/enums/largeShareholderConnectionType.enum';
import { Helpers } from '../../../utilities/helpers';

interface ConnectionDisplayItem {
  id: string;
  type: LargeShareholderConnectionType;
  name: string;
  entityId: string;
}

@Component({
  selector: 'app-large-shareholder-connections',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule
  ],
  templateUrl: './large-shareholder-connections.component.html',
  styleUrls: ['./large-shareholder-connections.component.scss']
})
export class LargeShareholderConnectionsComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() connections: LargeShareholderConnectionDto[] = [];
  @Output() deleteConnection = new EventEmitter<string>();

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private router = inject(Router);

  Helpers = Helpers;
  dataSource = new MatTableDataSource<ConnectionDisplayItem>([]);
  displayedColumns: string[] = ['name', 'type', 'actions'];

  ngOnInit() {
    this.setupCustomSorting();
    this.processData();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  private setupCustomSorting(): void {
    this.dataSource.sortingDataAccessor = (item: ConnectionDisplayItem, property: string) => {
      switch (property) {
        case 'type':
          return this.getConnectionTypeText(item.type);
        case 'name':
          return item.name;
        default:
          return (item as any)[property];
      }
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['connections']) {
      this.processData();
    }
  }

  private processData() {
    if (!this.connections || this.connections.length === 0) {
      this.dataSource.data = [];
      return;
    }

    const displayItems: ConnectionDisplayItem[] = this.connections.map(connection => ({
      id: connection.id,
      type: connection.type as LargeShareholderConnectionType,
      name: connection.name,
      entityId: connection.entityId
    }));

    this.dataSource.data = displayItems;
  }

  getConnectionTypeText(type: LargeShareholderConnectionType): string {
    switch (type) {
      case LargeShareholderConnectionType.Company:
        return 'Company';
      case LargeShareholderConnectionType.OpbCompany:
        return 'OPB Company';
      case LargeShareholderConnectionType.Director:
        return 'Director';
      default:
        return 'Unknown';
    }
  }

  onDelete(connectionId: string) {
    this.deleteConnection.emit(connectionId);
  }

  getEntityUrl(item: ConnectionDisplayItem): string {
    switch (item.type) {
      case LargeShareholderConnectionType.Director:
        return this.router.serializeUrl(this.router.createUrlTree(['/directors/edit', item.entityId]));
      case LargeShareholderConnectionType.Company:
        return this.router.serializeUrl(this.router.createUrlTree(['/companies/edit', item.entityId]));
      case LargeShareholderConnectionType.OpbCompany:
        return this.router.serializeUrl(this.router.createUrlTree(['/companies/opb-edit', item.entityId]));
      default:
        return '';
    }
  }
}
